import { FC, useRef, useState } from 'react';
import ContractForm from '../components/ContractForm';
import styles from './styles.module.css';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  createPostContractRequestFromFormData,
  useDeleteContractMutation,
  useEditContractMutation,
  useGetCollectionConstraintTypesQuery,
  useGetContractDetailQuery,
} from '../../../api/contractApi';
import { transformContractDetailToFormData } from '../../../types/contracts';
import Button from '../../../components/common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import PolicyRequired from '../../../components/common/PolicyRequired';
import { logger } from '../../../services/logger';
import HeaderLayout from '../../../components/HeaderLayout';
import Modal from '../../../components/common/Modal';
import { useGetWasteItemsQuery } from '../../../api/otherApi';
import { useGetDriverSkillLevelsQuery } from '../../../api/driverApi';

const EditContract: FC = () => {
  // 契約詳細が読み込みできたかどうかを保持する ref。
  // 削除後のレンダリングタイミングでのリフェッチを防ぐのが目的であり、
  // この値の変更で再レンダリングを起こす必要はないため、レンダリングと独立して設定可能な ref を使用
  const deleteRef = useRef(false);
  const { id } = useParams();
  const [edit] = useEditContractMutation();
  const navigate = useNavigate();
  const numId = Number(id);
  // 削除処理実行後に再度読み込まれるのを防ぐ
  const { data: formDetail } = useGetContractDetailQuery(numId, {
    skip: deleteRef.current,
  });
  const [deleter] = useDeleteContractMutation();
  const { data: wasteItems } = useGetWasteItemsQuery();
  const { data: constraintTypes } = useGetCollectionConstraintTypesQuery();
  const { data: skillLevels } = useGetDriverSkillLevelsQuery();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const openModal = () => setIsConfirmModalOpen(true);
  const onClose = () => {
    setIsConfirmModalOpen(false);
  };
  const onConfirm = () => {
    // navigate と同じタイミングで設定するとこのコンポーネントの再レンダリングとの race condition が解決しない可能性がある
    deleteRef.current = true;
    deleter(numId)
      .unwrap()
      .then(() => {
        navigate('/contracts');
      })
      .catch((e) => {
        // 削除失敗後は元に戻す
        deleteRef.current = false;
        logger.error(e);
      });
    setIsConfirmModalOpen(false);
  };
  const onBack = useBackFunction();

  return (
    <HeaderLayout
      title="受注入力"
      controls={
        <div className={styles.buttons}>
          <Button
            onClick={() => {
              navigate(`/contracts/copy/${id}`);
            }}
            variants={['secondary']}
          >
            <FontAwesomeIcon icon={faCopy} className={styles.ic} />
            複製
          </Button>
          <PolicyRequired policy={21}>
            <Button onClick={openModal} variants={['delete']}>
              <FontAwesomeIcon icon={faTrashCan} className={styles.ic} />
              削除
            </Button>
          </PolicyRequired>
        </div>
      }
    >
      {isConfirmModalOpen && (
        <ConfirmDeleteModal onRequestClose={onClose} onConfirm={onConfirm} />
      )}
      {formDetail && (
        <ContractForm
          initState={transformContractDetailToFormData(formDetail)}
          wasteItems={wasteItems}
          isEditable={formDetail.isEditable}
          constraintTypes={constraintTypes}
          skillLevels={skillLevels}
          onSubmit={(data) => {
            edit({
              id: numId,
              contract: createPostContractRequestFromFormData(data),
            })
              .unwrap()
              .then(() => {
                navigate('/contracts');
              })
              .catch(logger.error);
          }}
          onCancel={onBack}
        />
      )}
    </HeaderLayout>
  );
};

type ConfirmDeleteModalProps = {
  onRequestClose: () => void;
  onConfirm: () => void;
};
const ConfirmDeleteModal = ({
  onRequestClose,
  onConfirm,
}: ConfirmDeleteModalProps) => {
  return (
    <Modal onRequestClose={onRequestClose}>
      <div className={styles.modalHead}>本当に削除しますか？</div>
      <div className={styles.modalContent}>
        <div className={styles.modalText}>
          削除すると、元に戻すことはできません。
        </div>
        <div className={styles.modalButtons}>
          <div className={styles.modalButton}>
            <Button
              type="button"
              onClick={onRequestClose}
              variants={['fit', 'cancel']}
            >
              キャンセル
            </Button>
          </div>
          <div className={styles.modalButton}>
            <Button type="button" onClick={onConfirm} variants={['fit']}>
              確定
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const fromKey = 'from';
const backDestinations: {
  [key: string]: {
    path: string;
    getQueryParams: (searchParams: URLSearchParams) => string;
  };
} = {
  allocations: {
    path: '/allocations',
    getQueryParams(searchParams: URLSearchParams) {
      const span = searchParams.get('span') || '';
      const startDate = searchParams.get('startDate') || '';
      const endDate = searchParams.get('endDate') || '';
      const newParams = new URLSearchParams({
        span,
        startDate,
        endDate,
      });

      return `?${newParams}`;
    },
  },
};
const useBackFunction = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const from = searchParams.get(fromKey);

  if (!from) {
    return () => {
      navigate('/contracts');
    };
  }

  const destination = backDestinations[from];
  if (!destination) {
    return () => {
      navigate('/contracts');
    };
  }

  const destinationPath =
    destination.path + destination.getQueryParams(searchParams);

  return () => {
    navigate(destinationPath);
  };
};

export default EditContract;
